import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { isBusinessRole } from '../../../shared/helpers/role.helpers';
import { RolesService } from '../../../shared/services/roles.service';
import { RoleDTO } from '../../../shared/types/role.types';

@Component({
  selector: 'app-role-detail',
  imports: [
    CommonModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './role-detail.component.html',
})
export class RoleDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rolesService = inject(RolesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly role = signal<RoleDTO | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  readonly attributeEntries = computed<{ key: string; values: string[] }[]>(() => {
    const attrs = this.role()?.attributes;
    if (!attrs) return [];
    return Object.entries(attrs).map(([key, values]) => ({ key, values }));
  });

  readonly isBusinessRole = computed(() => {
    const n = this.role()?.name;
    return n ? isBusinessRole(n) : false;
  });

  private name = '';

  ngOnInit(): void {
    this.name = this.route.snapshot.paramMap.get('name') ?? '';
    if (!this.name) {
      this.loadError.set('Nom de rôle invalide.');
      this.loading.set(false);
      return;
    }
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.rolesService
      .get(this.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => { this.role.set(r); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(toErrorMessage(err, 'Impossible de charger ce rôle.'));
        },
      });
  }

  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.rolesService.delete(this.name).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/roles']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }
}
